import * as signalR from "@microsoft/signalr";
import { useEffect, useRef, useCallback } from "react";

const useHubConnection = (
    url: string,
    connections: (hubConnection: signalR.HubConnection) => void,
    onStart: () => Promise<void> | void = () => { }
) => {
    const connected = useRef(false);
    const hubConnectionRef = useRef<signalR.HubConnection | null>(null);

    // 👇 修复：使用 useRef 而不是 useCallback，避免依赖变化导致重复执行
    const connectionsRef = useRef(connections);

    // 👇 新增：同步更新 connectionsRef
    useEffect(() => {
        connectionsRef.current = connections;
    }, [connections]);

    // 👇 改进：稳定的 registerHandlers 函数
    const registerHandlers = useCallback((hubConnection: signalR.HubConnection) => {
        try {
            // 👇 先清除所有旧的监听器
            hubConnection.off("MeetingChanged");
            hubConnection.off("MeetingStarted");
            hubConnection.off("MeetingEnded");

            // 👇 使用 ref 而不是直接依赖 connections
            if (connectionsRef.current) {
                connectionsRef.current(hubConnection);
                console.log("✅ Event handlers registered successfully");
            }
        } catch (error) {
            console.error("❌ Failed to register handlers:", error);
        }
    }, []); // 👈 空依赖数组，因为使用 ref

    useEffect(() => {
        let isComponentMounted = true;
        let connectionAttempts = 0;
        const MAX_ATTEMPTS = 3;

        const setupConnection = async () => {
            // 👇 防止重复连接
            if (hubConnectionRef.current?.state === signalR.HubConnectionState.Connected) {
                console.log("Connection already established");
                return;
            }

            try {
                const hubConnection = new signalR.HubConnectionBuilder()
                    .configureLogging(signalR.LogLevel.Information)
                    .withAutomaticReconnect({
                        nextRetryDelayInMilliseconds: retryContext => {
                            if (retryContext.elapsedMilliseconds < 60000) {
                                return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
                            } else {
                                return null;
                            }
                        }
                    })
                    .withUrl(url)
                    .build();

                if (!isComponentMounted) return;

                hubConnectionRef.current = hubConnection;

                // 👇 首次连接时注册处理器
                registerHandlers(hubConnection);

                // 👇 重连事件处理
                hubConnection.onreconnecting(error => {
                    console.warn(`⚠️ SignalR reconnecting due to error: ${error}`);
                    connected.current = false;
                });

                hubConnection.onreconnected(connectionId => {
                    console.log(`✅ SignalR reconnected with connectionId: ${connectionId}`);
                    connected.current = true;

                    // 👇 重连后重新注册处理器
                    if (isComponentMounted && hubConnectionRef.current) {
                        registerHandlers(hubConnectionRef.current);
                    }
                });

                hubConnection.onclose(error => {
                    console.error(`❌ SignalR connection closed: ${error}`);
                    connected.current = false;
                });

                // 👇 启动连接
                await hubConnection.start();

                if (!isComponentMounted) return;

                connected.current = true;
                connectionAttempts = 0;
                console.log(`✅ SignalR connected successfully to ${url}`);

                if (onStart) {
                    await onStart();
                }
            } catch (error) {
                console.error(`❌ SignalR connection failed: ${error}`);

                connectionAttempts++;
                if (connectionAttempts < MAX_ATTEMPTS && isComponentMounted) {
                    console.log(`🔄 Retrying connection (${connectionAttempts}/${MAX_ATTEMPTS})...`);
                    setTimeout(setupConnection, 1000 * connectionAttempts);
                } else {
                    connected.current = false;
                }
            }
        };

        setupConnection();

        // 👇 清理函数
        return () => {
            isComponentMounted = false;

            if (hubConnectionRef.current) {
                hubConnectionRef.current.stop()
                    .then(() => {
                        console.log("✅ SignalR connection stopped");
                    })
                    .catch(error => {
                        console.error(`❌ Error stopping SignalR: ${error}`);
                    })
                    .finally(() => {
                        if (hubConnectionRef.current) {
                            hubConnectionRef.current = null;
                        }
                    });
                connected.current = false;
            }
        };
    }, [url, registerHandlers, onStart]); // 👈 registerHandlers 现在是稳定的

    return hubConnectionRef;
};

export default useHubConnection;